import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  Grid,
  AppBar,
  Toolbar,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  CircularProgress,
  InputAdornment,
  Stack,
  LinearProgress,
  Fade,
  Grow,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  Language as LanguageIcon,
  Description as DescriptionIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Logout as LogoutIcon,
  Work as WorkIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { keyframes } from "@mui/system";
import * as Sentry from "@sentry/react";
import axios from "axios";

// Floating animation for background circles
const floatAnimation = keyframes`
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  33% {
    transform: translateY(-20px) rotate(5deg);
  }
  66% {
    transform: translateY(10px) rotate(-3deg);
  }
`;

const pulseAnimation = keyframes`
  0%, 100% {
    opacity: 0.1;
    transform: scale(1);
  }
  50% {
    opacity: 0.15;
    transform: scale(1.05);
  }
`;

export const CompanyDetailsPage = ({
  onSubmit,
  onLogout,
  currentUser,
  allUsers = [],
  onImpersonate,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [formData, setFormData] = useState({
    username: "",
    companyName: "",
    role: "",
    companyUrl: "",
    description: "",
  });
  const [documents, setDocuments] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showUploadSection, setShowUploadSection] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [fetchingDetails, setFetchingDetails] = useState(true);

  const API_BASE = import.meta.env.VITE_API_BASE;

  useEffect(() => {
    setMounted(true);
    const fetchCompanyDetails = async () => {
      setFetchingDetails(true);
      try {
        const res = await axios.get(`${API_BASE}/get-company-data`, {
          withCredentials: true,
        });
        const result = res.data;
        // const res = await fetch(`${API_BASE}/get-company-data`, {
        //   method: "GET",
        //   credentials: "include",
        // });
        // const result = await res.json();
        if (result.success) {
          const { username, company_name, role, company_url, description } =
            result.company;
          setFormData({
            username,
            companyName: company_name,
            role,
            companyUrl: company_url,
            description,
          });
        }
      } catch (err) {
        if (err.response?.data?.message) {
          setError(err.response.data.message);
        } else {
          setError("Failed to load company details");
        }
        // Only log 500+ errors to Sentry
        if (err.response && err.response.status >= 500) {
          Sentry.captureException(err, {
            tags: { section: "company-details", source: "fetch-company" },
            extra: { status: err.response.status, response: err.response.data },
          });
        }
      } finally {
        setFetchingDetails(false);
      }
    };
    fetchCompanyDetails();
  }, []);

  // Update progress based on filled fields
  useEffect(() => {
    const filledFields = Object.values(formData).filter(
      (v) => v.trim() !== ""
    ).length;
    const newProgress = (filledFields / 5) * 100;
    setProgress(newProgress);
  }, [formData]);

  // Loading screen while fetching company details
  if (fetchingDetails) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#FAFAFA",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Card
          sx={{
            p: 5,
            borderRadius: 4,
            boxShadow: "0 8px 24px rgba(99, 102, 241, 0.1)",
            textAlign: "center",
            backgroundColor: "#fff",
            maxWidth: 400,
          }}
        >
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: 3,
              backgroundColor: "#6366F1",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px",
              boxShadow: "0 4px 12px rgba(99, 102, 241, 0.3)",
            }}
          >
            <Typography sx={{ fontSize: 24, color: "#fff" }}>🏢</Typography>
          </Box>
          <CircularProgress size={40} sx={{ color: "#6366F1", mb: 2 }} />
          <Typography
            variant="h6"
            sx={{ color: "#1F2937", fontWeight: 700, mb: 1 }}
          >
            Loading Company Details...
          </Typography>
          <Typography sx={{ color: "#6B7280" }}>
            Please wait while we retrieve your information
          </Typography>
        </Card>
      </Box>
    );
  }

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (documents.length + files.length > 5) {
      setError("Maximum 5 documents allowed");
      return;
    }
    setDocuments([...documents, ...files]);
    setError("");
  };

  const removeDocument = (index) => {
    setDocuments(documents.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });
      documents.forEach((file, index) => {
        formDataToSend.append(`document${index}`, file);
      });

      const response = await axios.post(`${API_BASE}/company`, formDataToSend, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });

      const result = response.data;
      // const response = await fetch(`${API_BASE}/company`, {
      //   method: "POST",
      //   body: formDataToSend,
      //   credentials: "include",
      // });

      // const result = await response.json();
      if (result.success) {
        const uploadedFilenames = result.uploaded_files.map((f) => f.filename);
        onSubmit({ ...formData, attachments: uploadedFilenames });
      } else {
        setError(result.message || "Failed to submit company details");
      }
    } catch (err) {
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError("Network error. Please try again.");
      }
      if (err.response && err.response.status >= 500) {
        Sentry.captureException(err, {
          tags: { section: "company-details", source: "submit-company" },
          extra: { status: err.response.status, response: err.response.data },
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
    if (error) setError(""); // Clear error when user starts typing
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (documents.length + files.length > 5) {
      setError("Maximum 5 documents allowed");
      return;
    }
    setDocuments([...documents, ...files]);
    setError("");
  };

  const getStepColor = (step) => {
    const filledFields = Object.values(formData).filter(
      (v) => v.trim() !== ""
    ).length;
    return filledFields >= step ? "#10B981" : "#E5E7EB";
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        minHeight: "100vh",
        bgcolor: "#FAFAFA",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Decorative background circles */}
      <Fade in={mounted} timeout={800}>
        <Box
          sx={{
            position: "absolute",
            width: 200,
            height: 200,
            borderRadius: "50%",
            backgroundColor: "#e3cfeb",
            opacity: 0.1,
            top: -50,
            right: -50,
            animation: `${floatAnimation} 8s ease-in-out infinite`,
            zIndex: 0,
          }}
        />
      </Fade>

      <Fade in={mounted} timeout={1000}>
        <Box
          sx={{
            position: "absolute",
            width: 150,
            height: 150,
            borderRadius: "50%",
            backgroundColor: "#10B981",
            opacity: 0.1,
            top: 200,
            left: -40,
            animation: `${pulseAnimation} 6s ease-in-out infinite`,
            zIndex: 0,
          }}
        />
      </Fade>

      <Fade in={mounted} timeout={1200}>
        <Box
          sx={{
            position: "absolute",
            width: 180,
            height: 180,
            borderRadius: "50%",
            backgroundColor: "#c2eea3",
            opacity: 0.1,
            bottom: 100,
            right: -60,
            animation: `${floatAnimation} 10s ease-in-out infinite`,
            animationDelay: "2s",
            zIndex: 0,
          }}
        />
      </Fade>

      <Container maxWidth="md" sx={{ mb: 4, position: "relative", zIndex: 1 }}>
        <Grow in={mounted} timeout={800}>
          <Box>
            {/* Header Section */}
            <Box sx={{ textAlign: "center", mb: 4, pt: 4 }}>
              <Box
                sx={{
                  width: 70,
                  height: 70,
                  borderRadius: 5,
                  backgroundColor: "#FFFFFF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 16px",
                  boxShadow: "0 8px 24px rgba(99, 102, 241, 0.2)",
                }}
              >
                <Typography sx={{ fontSize: 36 }}>🏢</Typography>
              </Box>
              <Typography
                variant={isMobile ? "h5" : "h4"}
                sx={{
                  fontWeight: "bold",
                  color: "#1F2937",
                  mb: 1,
                }}
              >
                Company Information
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "#6B7280",
                  lineHeight: 1.6,
                  px: { xs: 2, md: 0 },
                }}
              >
                Complete your profile to unlock AI-powered insights
              </Typography>
            </Box>

            {/* Progress Card */}
            <Fade in={mounted} timeout={1000}>
              <Card
                elevation={2}
                sx={{
                  borderRadius: 4,
                  mb: 3,
                  backgroundColor: "#FFFFFF",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        color: "#374151",
                      }}
                    >
                      Profile Completion
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "bold",
                        color: "#6366F1",
                      }}
                    >
                      {Math.round(progress)}%
                    </Typography>
                  </Box>

                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: "#E5E7EB",
                      mb: 2,
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: "#6366F1",
                        borderRadius: 4,
                      },
                    }}
                  />

                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    {[1, 2, 3, 4, 5].map((step) => (
                      <Box
                        key={step}
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          backgroundColor: "#F3F4F6",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: "50%",
                            backgroundColor: getStepColor(step),
                            transition: "all 0.3s ease",
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Fade>

            {/* Main Form Card */}
            <Fade in={mounted} timeout={1200}>
              <Card
                elevation={4}
                sx={{
                  borderRadius: 5,
                  backgroundColor: "#FFFFFF",
                  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.1)",
                }}
              >
                <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                  {error && (
                    <Grow in timeout={600}>
                      <Alert
                        severity="error"
                        sx={{
                          mb: 3,
                          borderRadius: 3,
                          backgroundColor: "#FEE2E2",
                          color: "#DC2626",
                          border: "none",
                          "& .MuiAlert-icon": {
                            color: "#DC2626",
                          },
                        }}
                      >
                        ⚠️ {error}
                      </Alert>
                    </Grow>
                  )}

                  <Box component="form" onSubmit={handleSubmit}>
                    <Grid container spacing={4} justifyContent="center">
                      <Grid
                        item
                        xs={12}
                        md={6}
                        sx={{ width: { xs: "100%", md: "50%" } }}
                      >
                        <Stack spacing={3} sx={{ width: "100%" }}>
                          <TextField
                            required
                            fullWidth
                            label="Username"
                            placeholder="Your Name"
                            value={formData.username}
                            onChange={handleChange("username")}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <PersonIcon sx={{ color: "#6366F1" }} />
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 3,
                                backgroundColor: "#FFFFFF",
                                transition: "all 0.3s ease",
                                "&:hover": {
                                  boxShadow:
                                    "0 4px 12px rgba(99, 102, 241, 0.1)",
                                },
                                "&.Mui-focused": {
                                  boxShadow:
                                    "0 4px 12px rgba(99, 102, 241, 0.2)",
                                },
                              },
                              "& .MuiInputLabel-root.Mui-focused": {
                                color: "#6366F1",
                              },
                            }}
                          />

                          <TextField
                            required
                            fullWidth
                            label="Company Name"
                            placeholder="e.g. Asambhav Solutions"
                            value={formData.companyName}
                            onChange={handleChange("companyName")}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <BusinessIcon sx={{ color: "#6366F1" }} />
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 3,
                                backgroundColor: "#FFFFFF",
                                transition: "all 0.3s ease",
                                "&:hover": {
                                  boxShadow:
                                    "0 4px 12px rgba(99, 102, 241, 0.1)",
                                },
                                "&.Mui-focused": {
                                  boxShadow:
                                    "0 4px 12px rgba(99, 102, 241, 0.2)",
                                },
                              },
                              "& .MuiInputLabel-root.Mui-focused": {
                                color: "#6366F1",
                              },
                            }}
                          />

                          <TextField
                            required
                            fullWidth
                            label="Your Role"
                            value={formData.role}
                            onChange={handleChange("role")}
                            placeholder="CEO, Manager, Developer, etc."
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <WorkIcon sx={{ color: "#6366F1" }} />
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 3,
                                backgroundColor: "#FFFFFF",
                                transition: "all 0.3s ease",
                                "&:hover": {
                                  boxShadow:
                                    "0 4px 12px rgba(99, 102, 241, 0.1)",
                                },
                                "&.Mui-focused": {
                                  boxShadow:
                                    "0 4px 12px rgba(99, 102, 241, 0.2)",
                                },
                              },
                              "& .MuiInputLabel-root.Mui-focused": {
                                color: "#6366F1",
                              },
                            }}
                          />

                          <TextField
                            fullWidth
                            label="Company Website"
                            placeholder="https://yourcompany.com"
                            type="text"
                            value={formData.companyUrl}
                            onChange={handleChange("companyUrl")}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <LanguageIcon sx={{ color: "#6366F1" }} />
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 3,
                                backgroundColor: "#FFFFFF",
                                transition: "all 0.3s ease",
                                "&:hover": {
                                  boxShadow:
                                    "0 4px 12px rgba(99, 102, 241, 0.1)",
                                },
                                "&.Mui-focused": {
                                  boxShadow:
                                    "0 4px 12px rgba(99, 102, 241, 0.2)",
                                },
                              },
                              "& .MuiInputLabel-root.Mui-focused": {
                                color: "#6366F1",
                              },
                            }}
                          />

                          <TextField
                            fullWidth
                            label="Brief Description"
                            placeholder="Describe what your company does, services, or focus area..."
                            multiline
                            rows={3}
                            value={formData.description}
                            onChange={handleChange("description")}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <DescriptionIcon sx={{ color: "#6366F1" }} />
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 3,
                                backgroundColor: "#FFFFFF",
                                transition: "all 0.3s ease",
                                "&:hover": {
                                  boxShadow:
                                    "0 4px 12px rgba(99, 102, 241, 0.1)",
                                },
                                "&.Mui-focused": {
                                  boxShadow:
                                    "0 4px 12px rgba(99, 102, 241, 0.2)",
                                },
                              },
                              "& .MuiInputLabel-root.Mui-focused": {
                                color: "#6366F1",
                              },
                            }}
                          />
                        </Stack>
                      </Grid>

                      {/* Right side with upload box */}
                      {showUploadSection && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="h6" gutterBottom>
                            Upload Documents (Max 5)
                          </Typography>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 3,
                              borderRadius: 3,
                              textAlign: "center",
                              border: "2px dashed #6366F1",
                              backgroundColor: isDragging
                                ? "#F0F4FF"
                                : "#F8FAFF",
                              cursor: "pointer",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                backgroundColor: "#F0F4FF",
                                borderColor: "#4F46E5",
                              },
                            }}
                            onClick={() =>
                              document.getElementById("file-upload").click()
                            }
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                          >
                            <CloudUploadIcon
                              sx={{ fontSize: 40, color: "#6366F1", mb: 1 }}
                            />
                            <Typography
                              variant="body1"
                              sx={{ color: "#1F2937", fontWeight: 600 }}
                            >
                              Drag and drop or click to upload
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              PDF, DOC, DOCX, JPG, PNG (max 10MB each)
                            </Typography>
                            <input
                              type="file"
                              multiple
                              onChange={handleFileUpload}
                              style={{ display: "none" }}
                              id="file-upload"
                              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            />
                          </Paper>

                          {documents.length > 0 && (
                            <Paper
                              variant="outlined"
                              sx={{ mt: 2, borderRadius: 3 }}
                            >
                              <List>
                                {documents.map((file, index) => (
                                  <ListItem
                                    key={index}
                                    divider={index < documents.length - 1}
                                    sx={{
                                      backgroundColor:
                                        index % 2 === 0 ? "#F9FAFB" : "#FFFFFF",
                                      "&:hover": { backgroundColor: "#F3F4F6" },
                                    }}
                                  >
                                    <ListItemText
                                      primary={
                                        <Typography
                                          variant="body2"
                                          noWrap
                                          sx={{
                                            maxWidth: 200,
                                            fontWeight: 600,
                                          }}
                                        >
                                          {file.name}
                                        </Typography>
                                      }
                                      secondary={
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          {formatFileSize(file.size)}
                                        </Typography>
                                      }
                                    />
                                    <ListItemSecondaryAction>
                                      <IconButton
                                        edge="end"
                                        color="error"
                                        onClick={() => removeDocument(index)}
                                        sx={{
                                          "&:hover": {
                                            backgroundColor: "#FEE2E2",
                                          },
                                        }}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </ListItemSecondaryAction>
                                  </ListItem>
                                ))}
                              </List>
                            </Paper>
                          )}
                        </Grid>
                      )}
                    </Grid>

                    <Box
                      sx={{ display: "flex", justifyContent: "center", mt: 4 }}
                    >
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={loading}
                        sx={{
                          px: 4,
                          py: 1.75,
                          fontSize: "1.1rem",
                          fontWeight: 600,
                          borderRadius: 3,
                          backgroundColor: "#6366F1",
                          boxShadow: "0 8px 24px rgba(99, 102, 241, 0.3)",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            backgroundColor: "#4F46E5",
                            transform: "translateY(-2px)",
                            boxShadow: "0 12px 32px rgba(99, 102, 241, 0.4)",
                          },
                          "&:active": {
                            transform: "translateY(0)",
                          },
                          "&:disabled": {
                            backgroundColor: "#9CA3AF",
                            transform: "none",
                          },
                        }}
                      >
                        {loading ? (
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <CircularProgress
                              size={20}
                              sx={{ color: "#fff", mr: 1 }}
                            />
                            Submitting...
                          </Box>
                        ) : (
                          "✨ Submit Details"
                        )}
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Fade>
          </Box>
        </Grow>
      </Container>
    </Box>
  );
};
